import { AlertTriangle, CheckCircle2, ChevronRight, Filter, Flame, ShieldCheck, WifiOff, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/shared/ui/Badge";
import { Button } from "@/shared/ui/Button";
import { Card, CardBody } from "@/shared/ui/Card";
import { EmptyState } from "@/shared/ui/EmptyState";
import { HoldTag } from "@/shared/ui/HoldTag";
import { Skeleton } from "@/shared/ui/Skeleton";
import { alertTone, type Level } from "@/shared/lib/levelTone";
import { STATE_LABEL_KEY, stateBadge } from "./alertMeta";
import {
  alertDurationS, alertTitle, clearedHow, formatDuration, isComm, kindLabel, plainMessage,
  riAtRaise, shortTime, whereLine,
} from "./alertText";
import { RiskBar } from "./RiskBar";
import type { Alert, AlertState } from "@/shared/api/models";

/**
 * FeedCard — 경보 목록(마스터). 경보 페이지와 실경보 오버레이(AlarmCenter)가 같이 쓴다.
 *
 * ## 2026-09-24 개편 — 「글자가 다 잘리고 숨겨진다」
 * 폰(CSS 폭 ≈ 384px)에서 행이 한 줄 `truncate` 3단이라 현장명·본문이 「테스트_금산2 (현장 기…」로
 * 잘렸고, 설비 무응답 경보는 「복합 가스 · 심각 RI 0.00」으로 찍혔다. 이제 행은:
 *   1줄  종류 아이콘 + **사람 말 제목**(「산소 경계」「현장 PC 응답 없음」) + 상태 + 시각
 *   2줄  어디서(현장 · 함체/장비) — 줄바꿈 허용
 *   3줄  무슨 일(내부 표기를 뗀 본문) — 줄바꿈 허용
 *   4줄  가스 = 위험도 막대(등급 구간 위 현재 값) / 통신 = 이어진 시간·해제 방식
 *   [확인][해제] — **아직 안 끝난 경보에만**. 해제된 경보에 흐린 버튼 두 개를 늘어놓던 것이
 *   목록의 절반을 먹었다.
 * 글자는 자르지 않는다(`break-keep` 로 단어 단위 줄바꿈). 잘라야 할 만큼 긴 건 상세에서 읽는다.
 *
 * Rows optionally carry their own 확인/해제 controls (`onAck` / `onClear`). The row body is a
 * separate button from the actions (no nested <button>), and the actions stop propagation so
 * acting never also re-selects.
 */
export function FeedCard({
  isLoading,
  isError,
  list,
  selectedId,
  onSelect,
  onAck,
  onClear,
  canAck = false,
  canClear = false,
  busyId = null,
  hiddenByFilter = false,
  hiddenCount = 0,
  onShowAll,
  groupByDay = false,
  showChevron = false,
  emptyTitle,
  emptyDesc,
}: {
  isLoading: boolean;
  isError: boolean;
  list: Alert[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Per-row acknowledge. Omit (or `canAck=false`) to hide the control. */
  onAck?: (id: number) => void;
  /** Per-row clear. Omit (or `canClear=false`) to hide the control. */
  onClear?: (id: number) => void;
  canAck?: boolean;
  canClear?: boolean;
  /** Alert id with a mutation in flight — its row controls show a spinner. */
  busyId?: number | null;
  /** 받아온 경보는 있는데 현재 필터로 0건인가 — 「경보 없음」과 구분해서 보여준다. */
  hiddenByFilter?: boolean;
  /** 필터 전 전체 건수(안내 문구용). */
  hiddenCount?: number;
  /** 「전체 보기」 버튼 — 필터를 풀어 준다. */
  onShowAll?: () => void;
  /** 날짜 머리줄(「오늘」「어제」「09-22 (월)」)로 묶는다 — 긴 이력 보기. */
  groupByDay?: boolean;
  /** 행 끝에 〉 — 누르면 상세 화면으로 넘어간다는 표시(폰). */
  showChevron?: boolean;
  emptyTitle?: string;
  emptyDesc?: string;
}) {
  const { t } = useTranslation();
  const showAck = canAck && !!onAck;
  const showClear = canClear && !!onClear;
  let lastDay = "";
  return (
    <Card>
      <CardBody className="p-0">
        {isLoading && (
          <div className="p-5 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        )}
        {isError && (
          <EmptyState
            Icon={AlertTriangle}
            title={t("widgets.alerts.feedCard.errorTitle")}
            description={t("widgets.alerts.feedCard.errorDesc")}
          />
        )}
        {/* ⚠️ 「경보가 없다」와 「필터가 가렸다」는 전혀 다른 상황이다. 예전에는 둘 다
            초록색 「이상 없음」으로 보여서, 하단 탭 배지에 27이 떠 있는데도 화면은
            「정상」이라고 말하는 상태가 됐다 — 안전 시스템에서 가장 위험한 종류의 화면이다. */}
        {hiddenByFilter && (
          <EmptyState
            tone="caution"
            Icon={Filter}
            title={t("widgets.alerts.feedCard.filteredTitle")}
            description={t("widgets.alerts.feedCard.filteredDesc", { n: hiddenCount ?? 0 })}
            action={
              onShowAll ? (
                <Button intent="outline" size="sm" onClick={onShowAll}>
                  {t("widgets.alerts.feedCard.showAll")}
                </Button>
              ) : undefined
            }
          />
        )}
        {!isLoading && !isError && !hiddenByFilter && list.length === 0 && (
          <EmptyState
            tone="success"
            Icon={ShieldCheck}
            title={emptyTitle ?? t("widgets.alerts.feedCard.emptyTitle")}
            description={emptyDesc ?? t("widgets.alerts.feedCard.emptyDesc")}
          />
        )}
        {!isLoading && !isError && list.length > 0 && (
          <ul role="list" className="divide-y divide-[var(--separator)]">
            {list.map((a) => {
              const day = groupByDay ? dayLabel(t, a.raised_at) : "";
              const header = groupByDay && day !== lastDay ? day : "";
              lastDay = day || lastDay;
              return (
                <li key={a.id} className="min-w-0">
                  {header && (
                    <div className="sticky top-0 z-[1] bg-[var(--bg-subtle)] px-4 py-1.5 text-[12px] font-semibold text-[var(--fg-muted)] lg:px-5">
                      {header}
                    </div>
                  )}
                  <AlertRow
                    a={a}
                    selected={selectedId === a.id}
                    onSelect={onSelect}
                    showAck={showAck}
                    showClear={showClear}
                    onAck={onAck}
                    onClear={onClear}
                    busy={busyId != null && busyId === a.id}
                    showChevron={showChevron}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function dayLabel(t: ReturnType<typeof useTranslation>["t"], iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  const p2 = (n: number) => String(n).padStart(2, "0");
  const md = `${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  const wd = t(`widgets.alerts.v2.weekday.${d.getDay()}`);
  if (d.toDateString() === today.toDateString()) return `${t("widgets.alerts.v2.today")} · ${md} (${wd})`;
  if (d.toDateString() === y.toDateString()) return `${t("widgets.alerts.v2.yesterday")} · ${md} (${wd})`;
  return `${md} (${wd})`;
}

/** 목록 한 행 — 오버레이·관제 피드도 이 모양을 따른다. */
export function AlertRow({
  a, selected = false, onSelect, showAck = false, showClear = false, onAck, onClear,
  busy = false, showChevron = false,
}: {
  a: Alert;
  selected?: boolean;
  onSelect: (id: number) => void;
  showAck?: boolean;
  showClear?: boolean;
  onAck?: ((id: number) => void) | undefined;
  onClear?: ((id: number) => void) | undefined;
  busy?: boolean;
  showChevron?: boolean;
}) {
  const { t } = useTranslation();
  const comm = isComm(a);
  const lv = (a.level ?? 0) as Level;
  const tone = alertTone(a, comm);   // 설비 무응답 = 회색 「설비」(등급색 아님)
  const Icon = comm ? WifiOff : Flame;
  const unresolved = a.state !== "cleared";
  const where = whereLine(t, a);
  const body = plainMessage(t, a);
  const how = clearedHow(a);
  const dur = alertDurationS(a);
  const actions = unresolved && (showAck || showClear);

  return (
    <div
      className={
        "transition-colors duration-[var(--motion-fast)] ease-[var(--ease-out-soft)] " +
        (selected ? "bg-[var(--bg-subtle)]" : "hover:bg-[var(--bg-hover)]")
      }
    >
      <button
        type="button"
        onClick={() => onSelect(a.id!)}
        aria-current={selected || undefined}
        className="flex w-full min-w-0 items-start gap-3 px-4 py-3 text-left lg:px-5"
      >
        <span
          aria-hidden
          className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-[9px]"
          style={{ background: `var(${tone.soft})`, color: `var(${tone.fg})` }}
        >
          <Icon size={16} strokeWidth={2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-start gap-2">
            <span className="min-w-0 flex-1 text-[14.5px] font-semibold leading-snug tracking-[-0.005em] break-keep">
              {alertTitle(t, a)}
            </span>
            <time className="shrink-0 pt-0.5 text-[12px] tabular-nums text-[var(--fg-subtle)]">
              {shortTime(a.raised_at)}
            </time>
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge tone={stateBadge(a.state as AlertState)}>
              {t(STATE_LABEL_KEY[a.state as AlertState])}
            </Badge>
            <span
              className="rounded-[6px] px-1.5 py-[1px] text-[11.5px] font-semibold"
              style={{ background: `var(${tone.soft})`, color: `var(${tone.fg})` }}
            >
              {t(tone.labelKey)}
            </span>
            <span className="text-[11.5px] text-[var(--fg-subtle)]">{kindLabel(t, a)}</span>
            {a.is_drill && <Badge tone="warning">{t("widgets.alerts.feedCard.drill")}</Badge>}
            {/* 보류 중인 가스가 한도(150 ppm) 이상이라 보류를 무시하고 낸 경보(2026-10-13). 위험의 크기는 옆 등급 칩이
                말하므로 이 꼬리표는 설비 회색 점선(보류 공통 색) — 등급색을 하나 더 세우면 등급이 두 번 읽힌다. */}
            {a.hold_override && (
              <HoldTag size="sm" title={t("widgets.alerts.feedCard.holdOverrideTitle")}>
                {t("widgets.alerts.feedCard.holdOverride")}
              </HoldTag>
            )}
          </span>
          {where && (
            <span className="mt-1 block text-[13px] font-medium leading-snug text-[var(--fg-muted)] break-keep">
              {where}
            </span>
          )}
          {body && (
            <span className="mt-0.5 block text-[13px] leading-snug text-[var(--fg-subtle)] break-keep line-clamp-3">
              {body}
            </span>
          )}
          {riAtRaise(a) != null && (
            <RiskBar className="mt-2 max-w-[360px]" value={riAtRaise(a)} level={lv}
                     label={t("widgets.alerts.v2.riskAtRaise")} showLevelText={false} />
          )}
          <span className="mt-1.5 block text-[12px] leading-snug text-[var(--fg-subtle)] tabular-nums">
            {a.state === "cleared"
              ? t(how === "auto" ? "widgets.alerts.v2.meta.clearedAuto" : "widgets.alerts.v2.meta.cleared", {
                  at: shortTime(a.cleared_at, false),
                  dur: formatDuration(t, dur),
                })
              : t("widgets.alerts.v2.meta.ongoing", { dur: formatDuration(t, dur) })}
          </span>
        </span>
        {showChevron && (
          <ChevronRight aria-hidden size={18} className="mt-2 shrink-0 text-[var(--fg-subtle)]" />
        )}
      </button>

      {actions && (
        <div className="flex items-center gap-2 px-4 pb-3 pl-[60px] lg:px-5 lg:pl-[64px] [&>*]:flex-1 sm:[&>*]:flex-none">
          {showAck && (
            <Button
              intent="primary"
              size="sm"
              loading={busy}
              disabled={busy || a.state !== "open"}
              onClick={(e) => {
                e.stopPropagation();
                onAck!(a.id!);
              }}
              leading={<CheckCircle2 size={14} strokeWidth={2} />}
              title={t("widgets.alerts.feedCard.ackTitle")}
            >
              {a.state === "open" ? t("widgets.alerts.feedCard.ack") : t("widgets.alerts.v2.acked")}
            </Button>
          )}
          {showClear && (
            <Button
              intent="outline"
              size="sm"
              loading={busy}
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                onClear!(a.id!);
              }}
              leading={<XCircle size={14} strokeWidth={2} />}
              title={comm ? t("widgets.alerts.v2.commClearTitle") : t("widgets.alerts.feedCard.clearTitle")}
            >
              {t("widgets.alerts.feedCard.clear")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
